import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "antd";
import { ArrowRightOutlined } from "@ant-design/icons";
import { motion } from "framer-motion";
import {
    solScaleIn,
    solSlideUp,
    solStagger,
    solViewport,
} from "../components/solutions/shared/solutionMotion";
// import { ServicePortfolioGallery } from "../components/solutions/shared/ServicePortfolioGallery";
import { PortfolioImageLightbox } from "../components/portfolio/PortfolioImageLightbox";
// import {
//     POST_EDITING_PORTFOLIO_SUBTITLE,
//     postEditingPortfolioItems,
// } from "../data/servicePortfolioGalleries";
import { postEditingServices } from "../data/postEditingServices";
import {
    postEditingCompare,
    postEditingCompareUsesFilteredPair,
} from "../data/postEditingCompare";
import "../styles/solution-pages-base.css";
import "../styles/service-portfolio.css";
import "../styles/post-editing-solution.css";

const workflowSteps = ["Concept", "Design", "Review", "Delivery"];

export const PostEditingPage = () => {
    const navigate = useNavigate();
    const [sliderPos, setSliderPos] = useState(50);
    const [activeServiceIndex, setActiveServiceIndex] = useState<number | null>(null);

    const serviceLightboxItems = postEditingServices.map((svc) => ({
        src: svc.image,
        alt: svc.alt,
    }));

    return (
        <div className="sol-page pe-page">
            {/* Section 1 – Hero with before/after comparison */}
            <section className="pe-hero">
                <div className="sol-container pe-hero__inner">
                    <motion.div
                        className="pe-hero__copy"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <div className="pe-hero-heading">
                            <h1 className="pe-hero-title">Post Editing</h1>
                            <p className="pe-hero-subtitle">
                                Content that looks <span className="pe-accent">publication-ready</span>
                            </p>
                        </div>
                        <p className="pe-lead">
                            Professional post editing that elevates your brand — from raw drafts to polished,
                            scroll-stopping creatives.
                        </p>
                    </motion.div>

                    <motion.div
                        className="pe-compare"
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <div
                            className={`pe-compare__stage${
                                postEditingCompareUsesFilteredPair ? " pe-compare__stage--filtered" : ""
                            }`}
                        >
                            <div className="pe-compare__pane pe-compare__pane--before">
                                <img
                                    className="pe-compare__img"
                                    src={postEditingCompare.before.src}
                                    alt={postEditingCompare.before.alt}
                                    draggable={false}
                                    loading="eager"
                                    decoding="async"
                                />
                                <span className="pe-compare__label pe-compare__label--before">Before</span>
                            </div>
                            <div
                                className="pe-compare__pane pe-compare__pane--after"
                                style={{
                                    left: `${sliderPos}%`,
                                    width: `${100 - sliderPos}%`,
                                }}
                            >
                                <div
                                    className="pe-compare__after-inner"
                                    style={{
                                        width: `${(100 / (100 - sliderPos)) * 100}%`,
                                        left: `${-(sliderPos / (100 - sliderPos)) * 100}%`,
                                    }}
                                >
                                    <img
                                        className="pe-compare__img"
                                        src={postEditingCompare.after.src}
                                        alt={postEditingCompare.after.alt}
                                        draggable={false}
                                        loading="eager"
                                        decoding="async"
                                    />
                                </div>
                                <span className="pe-compare__label pe-compare__label--after">After</span>
                            </div>
                            <input
                                type="range"
                                min={5}
                                max={95}
                                value={sliderPos}
                                onChange={(e) => setSliderPos(Number(e.target.value))}
                                className="pe-compare__input"
                                aria-label="Drag to compare before and after"
                            />
                            <div className="pe-compare__handle" style={{ left: `${sliderPos}%` }} aria-hidden />
                        </div>
                        <p className="pe-compare__hint">
                            Drag the slider to compare the unedited photo with the professionally enhanced version
                        </p>
                    </motion.div>
                </div>
            </section>
            {/* Section 2 – Editing Services Masonry */}
            <section className="pe-masonry-section">
                <div className="sol-container">
                    <motion.div
                        className="pe-section-head"
                        initial="hidden"
                        whileInView="visible"
                        viewport={solViewport}
                        variants={solSlideUp}
                    >
                        <h2 className="pe-h2">Editing Services</h2>
                        <p className="pe-sub">Creative formats tailored for every channel and campaign.</p>
                    </motion.div>

                    <motion.div
                        className="pe-masonry"
                        initial="hidden"
                        whileInView="visible"
                        viewport={solViewport}
                        variants={solStagger}
                    >
                        {postEditingServices.map((svc, i) => (
                            <motion.div
                                key={svc.name}
                                className={`pe-masonry__item pe-masonry__item--${svc.size}`}
                                variants={solScaleIn}
                                custom={i * 0.07}
                                style={{ "--pe-hue": svc.hue } as React.CSSProperties}
                            >
                                <button
                                    type="button"
                                    className="pe-masonry__thumb"
                                    onClick={() => setActiveServiceIndex(i)}
                                    aria-label={`View ${svc.name} sample — ${svc.alt}`}
                                >
                                    <img
                                        src={svc.image}
                                        alt={svc.alt}
                                        className="pe-masonry__img"
                                        loading="lazy"
                                        decoding="async"
                                    />
                                </button>
                                <h3>{svc.name}</h3>
                            </motion.div>
                        ))}
                    </motion.div>

                    {activeServiceIndex !== null && (
                        <PortfolioImageLightbox
                            items={serviceLightboxItems}
                            activeIndex={activeServiceIndex}
                            onClose={() => setActiveServiceIndex(null)}
                            onIndexChange={setActiveServiceIndex}
                        />
                    )}
                </div>
            </section>

            {/* Section 3 – Creative Workflow */}
            <section className="pe-workflow">
                <div className="sol-container">
                    <motion.div
                        className="pe-section-head pe-section-head--center"
                        initial="hidden"
                        whileInView="visible"
                        viewport={solViewport}
                        variants={solSlideUp}
                    >
                        <h2 className="pe-h2">Creative Workflow</h2>
                        <p className="pe-sub">Structured collaboration from brief to final delivery.</p>
                    </motion.div>

                    <div className="pe-workflow__track">
                        <svg className="pe-workflow__lines" viewBox="0 0 800 80" preserveAspectRatio="none" aria-hidden>
                            <motion.path
                                d="M 50 40 H 750"
                                fill="none"
                                stroke="url(#peGrad)"
                                strokeWidth="2"
                                strokeDasharray="8 6"
                                initial={{ pathLength: 0 }}
                                whileInView={{ pathLength: 1 }}
                                viewport={solViewport}
                                transition={{ duration: 1.2, ease: "easeOut" }}
                            />
                            <defs>
                                <linearGradient id="peGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                                    <stop offset="0%" stopColor="#002454" />
                                    <stop offset="100%" stopColor="#9e00ff" />
                                </linearGradient>
                            </defs>
                        </svg>
                        <motion.div
                            className="pe-workflow__steps"
                            initial="hidden"
                            whileInView="visible"
                            viewport={solViewport}
                            variants={solStagger}
                        >
                            {workflowSteps.map((step) => (
                                <motion.div key={step} className="pe-workflow__step" variants={solScaleIn}>
                                    <div className="pe-workflow__dot" />
                                    <span>{step}</span>
                                </motion.div>
                            ))}
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Our Work section — temporarily hidden
            <ServicePortfolioGallery
                items={postEditingPortfolioItems}
                subtitle={POST_EDITING_PORTFOLIO_SUBTITLE}
                sectionId="pe-portfolio"
            />
            */}

            <section className="sol-cta">
                <div className="sol-container">
                    <motion.div
                        className="sol-cta__inner"
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={solViewport}
                    >
                        <h2 className="sol-cta__title">Need polished social content?</h2>
                        <p className="sol-cta__desc">
                            Send us your raw assets — we&apos;ll return on-brand, ready-to-publish creatives.
                        </p>
                        <Button className="sol-btn" onClick={() => navigate("/contact")}>
                            Request Post Editing <ArrowRightOutlined />
                        </Button>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};
